import React from "react";

const CoverageAndSupportSection = () => {
  return (
    <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
      {/* Background Decoration (Matching Hero) */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-[100px] translate-x-1/2 -translate-y-1/2"></div>

      <div className="container mx-auto px-6 max-w-screen-xl relative z-10">
        <div className="flex flex-col lg:flex-row gap-16">
          {/* --- Left Side: Coverage & Map --- */}
          <div className="lg:w-1/2 w-full">
            <span className="text-blue-400 font-bold tracking-wider uppercase text-xs">
              Our Network
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mt-2 mb-6">
              Expanding Coverage <br /> Daily
            </h2>
            <p className="text-slate-400 mb-8 leading-relaxed text-lg">
              We are actively laying fiber-optic cables across the city. Check
              our map to see if your neighborhood is connected, or sign up for
              updates when we arrive.
            </p>

            {/* Mock Map Image */}
            <div className="rounded-2xl overflow-hidden border border-slate-700 shadow-2xl h-64 md:h-80 relative group">
              {/* Using a generic map-like abstract image or actual map placeholder */}
              <img
                src="https://images.unsplash.com/photo-1524661135-423995f22d0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Network Coverage Map"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-blue-900/20"></div>
            </div>
          </div>

          {/* --- Right Side: Support Methods --- */}
          <div className="lg:w-1/2 w-full flex flex-col justify-center">
            <span className="text-blue-400 font-bold tracking-wider uppercase text-xs">
              Need Help?
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mt-2 mb-10">
              We Are Here 24/7
            </h2>

            <div className="space-y-6">
              {/* Support Card 1: Call */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 rounded-2xl hover:bg-white/10 transition-colors flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-600/20 rounded-full flex items-center justify-center text-blue-400 flex-shrink-0">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-1">Call Us</h3>
                  <p className="text-slate-400 text-sm">
                    Mon-Fri from 8am to 9pm
                  </p>
                  <p className="text-white font-mono text-lg mt-1">
                    +98 (78)123-4567
                  </p>
                </div>
              </div>

              {/* Support Card 2: Chat */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 rounded-2xl hover:bg-white/10 transition-colors flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-600/20 rounded-full flex items-center justify-center text-blue-400 flex-shrink-0">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-1">Live Chat</h3>
                  <p className="text-slate-400 text-sm">
                    Average wait time: 2 mins
                  </p>
                  <p className="text-white font-bold mt-1 cursor-pointer hover:text-blue-400 transition-colors">
                    Start Chat Now &rarr;
                  </p>
                </div>
              </div>

              {/* Support Card 3: Visit */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 rounded-2xl hover:bg-white/10 transition-colors flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-600/20 rounded-full flex items-center justify-center text-blue-400 flex-shrink-0">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-1">Visit Sales Office</h3>
                  <p className="text-slate-400 text-sm">
                    Get a free demo of our service
                  </p>
                  <p className="text-white mt-1">Mazar-e-Sharif, Afghanistan</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoverageAndSupportSection;
